<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link view="AdminBatchInquiryList" label="Batch Reports" :active="true" />
    </template>

    <div class="box">
      <div class="level">
        <div class="level-left">
          <div>
            <h1 class="title">Batch Reports</h1>
            <p class="subtitle is-6 has-text-grey">
              Every batch report run on the platform, most recent first. Open one to see its
              rows or close it for a customer.
            </p>
          </div>
        </div>
      </div>

      <hr />

      <div class="mb-4 filters-row">
        <div class="columns is-variable is-4">
          <div class="column">
            <b-field label="Organization">
              <b-select v-model="selectedOrg" placeholder="All Organizations" @input="onFilterChange" expanded>
                <option :value="0">All Organizations</option>
                <option v-for="org in organizationOptions" :value="org.id" :key="org.id">
                  {{ org.name }}
                </option>
              </b-select>
            </b-field>
          </div>

          <div class="column">
            <b-field label="Status">
              <b-select v-model="selectedStatus" placeholder="All Statuses" @input="onFilterChange" expanded>
                <option value="">All Statuses</option>
                <option v-for="status in statusOptions" :value="status" :key="status">
                  {{ status }}
                </option>
              </b-select>
            </b-field>
          </div>

          <div class="column is-narrow">
            <b-button type="is-primary" @click="resetFilters">Clear</b-button>
          </div>
        </div>
      </div>

      <b-table
        :data="batches"
        :loading="loading"
        striped
        hoverable
        paginated
        backend-pagination
        :total="total"
        :per-page="perPage"
        :current-page="page"
        @page-change="onPageChange"
        @click="openBatchInquiry"
      >
        <b-table-column field="id" label="Batch" v-slot="props">#{{ props.row.id }}</b-table-column>

        <b-table-column field="status" label="Status" v-slot="props">
          <span class="tag" :class="statusClass(props.row.status)">{{ props.row.status }}</span>
        </b-table-column>

        <b-table-column field="progress" label="Progress" v-slot="props">
          {{ props.row.completed }} / {{ props.row.total }}
        </b-table-column>

        <b-table-column field="user" label="User" v-slot="props">
          <span style="white-space: pre-line;">
            {{ formatUser(props.row.user) }}
          </span>
        </b-table-column>

        <b-table-column field="organization" label="Organization" v-slot="props">
          {{ props.row.organization?.name || '-' }}
        </b-table-column>

        <b-table-column field="created_at" label="Created" v-slot="props">
          <span style="white-space: pre-line;">
            {{ formatDate(props.row.created_at) }}
          </span>
        </b-table-column>

        <template #empty>
          <section class="section has-text-centered">
            <b-icon icon="file-alert" size="is-large"></b-icon>
            <p class="mt-2">No batch reports found for this selection</p>
          </section>
        </template>
      </b-table>
    </div>
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';

export default {
  name: 'AdminBatchInquiryList',
  components: { AdminLayout },
  data() {
    return {
      batches: [],
      total: 0,
      page: 1,
      perPage: 15,
      loading: false,
      organizationOptions: [],
      selectedOrg: null,
      selectedStatus: '',
      statusOptions: ['pending', 'running', 'complete', 'canceled'],
    };
  },
  created() {
    this.fetchOrganizations();
    this.fetchBatches();
  },
  methods: {
    onFilterChange() {
      this.page = 1;
      this.fetchBatches();
    },
    onPageChange(page) {
      this.page = page;
      this.fetchBatches();
    },
    resetFilters() {
      this.selectedOrg = null;
      this.selectedStatus = '';
      this.page = 1;
      this.fetchBatches();
    },
    async fetchOrganizations() {
      try {
        const response = await request.get('/admin/organizations');
        this.organizationOptions = response.data.organizations.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      } catch {
        this.$buefy.toast.open({ message: 'Failed to load organizations', type: 'is-danger' });
      }
    },
    async fetchBatches() {
      try {
        this.loading = true;
        const params = { limit: this.perPage, page: this.page };
        if (this.selectedOrg) params.organization_id = this.selectedOrg;
        if (this.selectedStatus) params.status = this.selectedStatus;
        const response = await request.get('/admin/batch-inquiries', { params });
        this.batches = response.data.batches || [];
        this.total = parseInt(response.headers['x-total-count'], 10) || 0;
      } catch {
        this.$buefy.toast.open({ message: 'Failed to load batch reports', type: 'is-danger' });
      } finally {
        this.loading = false;
      }
    },
    openBatchInquiry(row) {
      this.$router.push({ name: 'AdminBatchInquiryReport', params: { id: row.id } });
    },
    statusClass(status) {
      return {
        'is-success': status === 'complete',
        'is-info': status === 'running',
        'is-light': status === 'pending' || status === 'canceled',
      };
    },
    formatUser(user) {
      if (!user) return '-';
      return `${user.first_name} ${user.last_name}\n${user.email}`;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${month}/${day}/${year}\n${hours}:${minutes} ${ampm}`;
    },
  },
};
</script>
